/*
 * IntegrationViewList.tsx  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useState } from "react";
import { BEM, someIncludesCaseInsensitive, useTranslate } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import { IntegrationEditorCreate } from "../IntegrationEditorCreate";
import { IEditorApiIntegrationResponse } from "@adapptio/lib-core";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";
import { List } from "../../../../ui/components/List";
import { IntegrationEditorCreateListItem } from "./IntegrationEditorCreateListItem";
import { Panel } from "../../../../ui/components/Panel";
import { ISearchBoxProps, SearchBox, SEARCHBOX_SIZE } from "../../../../ui/components/SearchBox";
import { IIntegrationType } from "../../../../integration/IntegrationServiceTypes";
import { useAbility } from "../../../../ui/hooks/useAbility";
import { ABILITY_ACTIONS } from "../../../../ability/AbilityService";

export interface IIntegrationEditorCreateListProps {
	view: IntegrationEditorCreate;
	className?: string;
	selectedIntegration?: IEditorApiIntegrationResponse;
	onIntegrationSelect?: (integrationType: IEditorApiIntegrationResponse) => void;
}

export const IntegrationEditorCreateList: React.FC<IIntegrationEditorCreateListProps> = ({
	view,
	className,
	selectedIntegration,
	onIntegrationSelect
}) => {
	const t = useTranslate();

	const ability = useAbility();

	const [ integrationTypes, setIntegrationTypes ] = useState<IIntegrationType[]>(() =>
		view.getIntegrationTypes()
	);
	const [ search, setSearch ] = useState("");

	const baseClass = BEM("edt-integration-editor-create-list");

	const integrationCategories = useMemo(
		() =>
			integrationTypes.reduce((acc, intgType) => {
				if (
					ability.can(ABILITY_ACTIONS.READ, intgType) &&
					(!search ||
						someIncludesCaseInsensitive(search, [
							intgType.integrationType,
							intgType.label,
							intgType.description
						]))
				) {
					acc[intgType.category] = acc[intgType.category] || [];
					acc[intgType.category].push(intgType);
				}
				return acc;
			}, {} as Record<string, IIntegrationType[]>),
		[ integrationTypes, search, ability ]
	);

	const getOnClickHandler = useCallback(
		(item: IIntegrationType) => () => {
			if (isFunction(onIntegrationSelect)) {
				onIntegrationSelect(item as unknown as IEditorApiIntegrationResponse); // Type hack
			}
		},
		[ onIntegrationSelect ]
	);

	const handleSearchChange = useCallback<ISearchBoxProps["onChange"]>(
		(value) => {
			setSearch(value);
		},
		[ setSearch ]
	);

	if (className) {
		baseClass.add(className);
	}

	useSimpleEvent(view.onIntegrationTypesChanged, () => {
		setIntegrationTypes(view.getIntegrationTypes);
	});

	return (
		<Panel className={baseClass.toClassName()}>
			<div className={baseClass.element("header").toClassName()}>
				<div className={baseClass.element("title").toClassName()}>
					{t("editor", "Create Integration")}
				</div>
				<div className={baseClass.element("search-wrapper").toClassName()}>
					<SearchBox
						value={search}
						onChange={handleSearchChange}
						placeholder={t("editor", "Search integrations...")}
						className={baseClass.element("search").toClassName()}
						size={SEARCHBOX_SIZE.LARGE}
					/>
				</div>
			</div>

			<div className={baseClass.element("content").toClassName()}>
				<List className={baseClass.element("list").toClassName()}>
					{Object.keys(integrationCategories).map((categoryKey) => {
						const integrationTypes = integrationCategories[categoryKey];

						return (
							<div key={categoryKey} className={baseClass.element("category").toClassName()}>
								<div className={baseClass.element("category-title").toClassName()}>
									<span className={baseClass.element("category-title-text").toClassName()}>
										{categoryKey}
									</span>
								</div>
								{integrationTypes.map((item) => (
									<IntegrationEditorCreateListItem
										key={item.integrationType}
										onClick={getOnClickHandler(item)}
										icon={item.icon}
										label={item.label}
										description={item.description}
										selected={
											selectedIntegration?.integrationType === item.integrationType
										}
									/>
								))}
							</div>
						);
					})}
				</List>
			</div>
		</Panel>
	);
};
